import React, { useState, useEffect, useRef, useCallback } from "react";
import { Capacitor } from "@capacitor/core";

const AVATARS = ["🦊","👾","🦖","🐱","🦁","🐧","🐷","🐬","👽","👻"];
const AV_COLORS = ["#ff3e3e","#9b6dff","#3effa3","#f5c842","#ff6b3e","#3eaaff","#ff3eb5","#3effee","#aaff3e","#ffffff"];

function avStyle(idx) {
  const c = AV_COLORS[idx % AV_COLORS.length];
  return { emoji: AVATARS[idx % AVATARS.length], color: c, bg: c + "22", border: c };
}

const _isNative = Capacitor.isNativePlatform();
const WS_URL = process.env.REACT_APP_WS_URL ||
  (_isNative
    ? "wss://app.vitornorton.com.br/impostor-api"
    : window.location.hostname === "localhost"
    ? "ws://localhost:3001"
    : `${window.location.protocol === "https:" ? "wss" : "ws"}://${window.location.host}/impostor-api`);

const API_BASE = process.env.REACT_APP_API_URL ||
  (_isNative
    ? "https://app.vitornorton.com.br/impostor-api"
    : window.location.hostname === "localhost"
    ? "http://localhost:3001"
    : `${window.location.protocol}//${window.location.host}/impostor-api`);

const MODOS = [
  { id: "perguntas",       label: "❓ Modo Perguntas" },
  { id: "palavra-secreta", label: "🔐 Palavra Secreta" },
  { id: "duas-faccoes",    label: "⚔️ Duas Facções" },
  { id: "descubra-nota",   label: "🎯 Descubra a Nota" },
  { id: "adivinhe-musica", label: "🎵 Adivinhe a Música" },
  { id: "mega-senha",      label: "🏆 Mega Senha" },
];

const ROUND_START_FASES = ["PERGUNTA_JOGADOR","PALAVRA_REVEAL","FACCAO_REVEAL","NOTA_REVEAL","MUSICA_BUSCAR","MEGASENHA_REVEAL"];
const FASES_SEM_INSTRUCAO = ["RESULTADOS","PLACAR_FINAL","NOTA_RESULTADO","MUSICA_RESULTADO","MEGASENHA_RESULTADO"];

function PerfilForm({ nome, setNome, avatarIdx, setAvatarIdx, erro, setErro, carregando, botaoLabel, onSubmit, onVoltar, extras }) {
  return (
    <div className="app-wrapper" style={{ paddingTop: 88 }}>
      <div className="card" style={{ maxWidth: 420 }}>
        <div className="card-header">
          <div className="title-eyebrow">👤 Seu perfil</div>
          <h1 className="title-main" style={{ fontSize: "2rem" }}>Como<br /><span style={{ color: "var(--purple)" }}>se chamar?</span></h1>
        </div>
        <div className="card-body" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div className="input-group">
            <label className="input-label">Seu nome</label>
            <input className="input-field" placeholder="Ex: Maria" value={nome}
              onChange={e => { setNome(e.target.value); setErro(""); }} maxLength={20} autoFocus />
          </div>
          <div className="input-group">
            <label className="input-label">Avatar</label>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {AVATARS.map((av, i) => {
                const av2 = avStyle(i);
                return (
                  <button key={i} onClick={() => setAvatarIdx(i)} style={{
                    width: 44, height: 44, borderRadius: 10,
                    border: `2px solid ${avatarIdx === i ? av2.border : "var(--border)"}`,
                    background: avatarIdx === i ? av2.bg : "var(--bg-elevated)",
                    fontSize: "1.4rem", cursor: "pointer",
                  }}>{av}</button>
                );
              })}
            </div>
          </div>
          {extras}
          {erro && <div style={{ color: "var(--accent)", fontSize: "0.85rem" }}>⚠️ {erro}</div>}
        </div>
        <div className="card-footer">
          <button className="btn btn-ghost" onClick={onVoltar}>← Voltar</button>
          <button className="btn btn-primary" onClick={onSubmit} disabled={carregando}>
            {carregando ? "Conectando..." : botaoLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function OnlineLobby({ onBack }) {
  const [tela, setTela] = useState("menu");
  const [nome, setNome] = useState("");
  const [avatarIdx, setAvatarIdx] = useState(0);
  const [codigoInput, setCodigoInput] = useState("");
  const [modoJogo, setModoJogo] = useState("perguntas");
  const [erro, setErro] = useState("");
  const [carregando, setCarregando] = useState(false);

  const [sala, setSala] = useState(null);
  const [jogadorId, setJogadorId] = useState(null);
  const [instrucaoPrivada, setInstrucaoPrivada] = useState(null);
  const [faseJogo, setFaseJogo] = useState(null);
  const [dadosJogo, setDadosJogo] = useState(null);

  const wsRef = useRef(null);
  const avatar = AVATARS[avatarIdx];

  const conectarWS = useCallback(() => {
    return new Promise((resolve, reject) => {
      try {
        const ws = new WebSocket(WS_URL);
        wsRef.current = ws;
        ws.onopen = () => resolve(ws);
        ws.onerror = () => reject(new Error("Não foi possível conectar ao servidor."));
        ws.onmessage = (event) => handleMensagem(JSON.parse(event.data));
        ws.onclose = () => setErro("Conexão encerrada. Atualize a página para reconectar.");
      } catch (e) { reject(e); }
    });
  }, []); // eslint-disable-line

  const handleMensagem = (msg) => {
    switch (msg.tipo) {
      case "CONECTADO":
        setJogadorId(msg.jogadorId);
        break;
      case "SALA_CRIADA":
      case "ENTROU_SALA":
        setSala(msg.estadoPublico);
        setTela("sala");
        setCarregando(false);
        break;
      case "JOGADOR_ENTROU":
      case "JOGADOR_SAIU":
      case "ESTADO_ATUALIZADO":
      case "NOVO_HOST":
        setSala(msg.estadoPublico);
        break;
      case "INSTRUCAO_PRIVADA":
      case "nota":
      case "musica":
      case "megasenha":
      case "faccao":
      case "palavra":
        setInstrucaoPrivada(msg);
        break;
      case "FASE_ATUALIZADA": {
        setSala(msg.estadoPublico);
        setFaseJogo(msg.fase);
        setTela("jogo");
        const { tipo: _t, fase: _f, estadoPublico: _e, ...extra } = msg;
        if (ROUND_START_FASES.includes(msg.fase)) {
          // NÃO limpa instrucaoPrivada aqui — ela chegou antes e é da rodada atual
          setDadosJogo(Object.keys(extra).length > 0 ? extra : null);
        } else {
          setDadosJogo(prev => ({ ...(prev || {}), ...extra }));
        }
        break;
      }
      case "FASE_RESULTADOS":
        setFaseJogo("RESULTADOS");
        setDadosJogo({ respostas: msg.respostas, pergunta: msg.pergunta });
        setSala(msg.estadoPublico);
        break;
      case "VOTO_REGISTRADO":
        setDadosJogo(prev => ({ ...prev, votosRecebidos: msg.total, esperando: msg.esperando }));
        break;
      case "RESULTADO_FINAL":
        setFaseJogo("PLACAR_FINAL");
        setDadosJogo({ votos: msg.votos, contagem: msg.contagem, impostoresIdx: msg.impostoresIdx, jogadores: msg.jogadores });
        break;
      case "NOTA_CHUTE_RESULTADO":
        setDadosJogo(prev => ({
          ...prev,
          ultimoChute: { chute: msg.chute, acertou: msg.acertou, adivinhadorNome: msg.adivinhadorNome, adivinhadorIdx: msg.adivinhadorIdx },
          turnoAdiv: msg.turnoAdiv,
          adivinhadorAtual: msg.proximo,
        }));
        break;
      case "NOTA_RESULTADO_FINAL":
        setFaseJogo("NOTA_RESULTADO");
        setDadosJogo({ nota: msg.nota, chutes: msg.chutes, pares: msg.pares });
        setSala(msg.estadoPublico);
        break;
      case "MUSICA_PALPITE_RECEBIDO":
        setDadosJogo(prev => ({ ...prev, palpitePendente: { jogadorId: msg.jogadorId, jogadorNome: msg.jogadorNome, palpite: msg.palpite } }));
        break;
      case "MUSICA_PALPITE_ERRADO":
        setDadosJogo(prev => ({ ...prev, palpitePendente: null, ultimoPalpiteErrado: { jogadorId: msg.jogadorId } }));
        break;
      case "MEGASENHA_DICA_ADICIONADA":
        setDadosJogo(prev => ({
          ...prev,
          dicas: msg.dicas,
          turnoParIdx: msg.turnoParIdx,
          dadorAtual: msg.dadorAtual,
        }));
        break;
      case "ERRO":
        setErro(msg.mensagem);
        setCarregando(false);
        break;
      default: break;
    }
  };

  const enviar = (tipo, dados = {}) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ tipo, ...dados }));
    }
  };

  const handleCriarSala = async () => {
    if (!nome.trim()) { setErro("Digite seu nome!"); return; }
    setCarregando(true); setErro("");
    try {
      const ws = await conectarWS();
      ws.addEventListener("message", function handler(event) {
        const msg = JSON.parse(event.data);
        if (msg.tipo === "CONECTADO") {
          ws.removeEventListener("message", handler);
          ws.send(JSON.stringify({ tipo: "CRIAR_SALA", nome: nome.trim(), avatar, modoJogo }));
        }
      });
    } catch (e) { setErro(e.message); setCarregando(false); }
  };

  const handleEntrarSala = async () => {
    if (!nome.trim()) { setErro("Digite seu nome!"); return; }
    if (!codigoInput.trim()) { setErro("Digite o código da sala!"); return; }
    setCarregando(true); setErro("");
    try {
      const ws = await conectarWS();
      ws.addEventListener("message", function handler(event) {
        const msg = JSON.parse(event.data);
        if (msg.tipo === "CONECTADO") {
          ws.removeEventListener("message", handler);
          ws.send(JSON.stringify({ tipo: "ENTRAR_SALA", nome: nome.trim(), avatar, codigo: codigoInput.trim().toUpperCase() }));
        }
      });
    } catch (e) { setErro(e.message); setCarregando(false); }
  };

  const isHost = sala && jogadorId && sala.hostId === jogadorId;

  useEffect(() => { return () => { wsRef.current?.close(); }; }, []);

  // ── MENU ────────────────────────────────────────────────────────
  if (tela === "menu") return (
    <div className="app-wrapper" style={{ paddingTop: 88 }}>
      <div className="card" style={{ maxWidth: 420 }}>
        <div className="card-header">
          <div className="title-eyebrow">🌐 Modo Online</div>
          <h1 className="title-main">Jogar<br /><span style={{ color: "var(--purple)" }}>Online</span></h1>
          <p className="title-sub">Jogue com amigos à distância, cada um no seu celular.</p>
        </div>
        <div className="card-body" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <button className="btn btn-primary btn-full" onClick={() => setTela("criar")}>🏠 Criar sala</button>
          <button className="btn btn-secondary btn-full" onClick={() => setTela("entrar")}>🚪 Entrar numa sala</button>
        </div>
        <div className="card-footer">
          <button className="btn btn-ghost" onClick={onBack}>← Voltar</button>
        </div>
      </div>
    </div>
  );

  if (tela === "criar") return (
    <PerfilForm
      nome={nome} setNome={setNome} avatarIdx={avatarIdx} setAvatarIdx={setAvatarIdx}
      erro={erro} setErro={setErro} carregando={carregando}
      botaoLabel="🏠 Criar sala →" onSubmit={handleCriarSala}
      onVoltar={() => { setTela("menu"); setErro(""); }}
      extras={
        <div className="input-group">
          <label className="input-label">Modo de jogo</label>
          <select className="input-field" value={modoJogo} onChange={e => setModoJogo(e.target.value)} style={{ cursor: "pointer" }}>
            {MODOS.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
          </select>
        </div>
      }
    />
  );

  if (tela === "entrar") return (
    <PerfilForm
      nome={nome} setNome={setNome} avatarIdx={avatarIdx} setAvatarIdx={setAvatarIdx}
      erro={erro} setErro={setErro} carregando={carregando}
      botaoLabel="🚪 Entrar →" onSubmit={handleEntrarSala}
      onVoltar={() => { setTela("menu"); setErro(""); }}
      extras={
        <div className="input-group">
          <label className="input-label">Código da sala</label>
          <input className="input-field" placeholder="Ex: AB3XY" value={codigoInput}
            onChange={e => { setCodigoInput(e.target.value.toUpperCase()); setErro(""); }}
            maxLength={5} style={{ fontFamily: "'Bebas Neue'", fontSize: "1.8rem", letterSpacing: "0.2em", textAlign: "center" }} />
        </div>
      }
    />
  );

  // ── LOBBY ───────────────────────────────────────────────────────
  if (tela === "sala" && sala) {
    const podeIniciar = sala.jogadores.length >= 2;
    return (
      <div className="app-wrapper" style={{ paddingTop: 88, paddingBottom: 32 }}>
        <div className="card" style={{ maxWidth: 480 }}>
          <div className="card-header">
            <div className="title-eyebrow">🌐 Sala Online</div>
            <h1 className="title-main" style={{ fontSize: "2.4rem" }}>
              <span style={{ color: "var(--purple)", fontFamily: "'Space Mono'", letterSpacing: "0.2em" }}>{sala.codigo}</span>
            </h1>
            <p className="title-sub">Compartilhe o código com seus amigos.</p>
          </div>
          <div className="card-body" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ background: "var(--bg-elevated)", borderRadius: 12, padding: "14px 16px" }}>
              <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", textTransform: "uppercase", marginBottom: 10 }}>
                👥 Jogadores ({sala.jogadores.length}/10)
              </div>
              {sala.jogadores.map((j, i) => {
                const av = avStyle(i);
                return (
                  <div key={j.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0", borderBottom: "1px solid var(--border)" }}>
                    <div style={{ width: 36, height: 36, borderRadius: "50%", background: av.bg, border: `2px solid ${av.border}`, color: av.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.2rem" }}>{j.avatar}</div>
                    <span style={{ fontWeight: 700, color: "var(--text-primary)", flex: 1 }}>{j.nome}</span>
                    {j.id === sala.hostId && <span style={{ fontSize: "0.7rem", color: "var(--gold)", background: "rgba(245,200,66,0.15)", border: "1px solid var(--gold)", borderRadius: 6, padding: "2px 8px" }}>HOST</span>}
                    {j.id === jogadorId && <span style={{ fontSize: "0.7rem", color: "var(--purple)", background: "rgba(155,109,255,0.15)", border: "1px solid var(--purple)", borderRadius: 6, padding: "2px 8px" }}>VOCÊ</span>}
                    <span style={{ fontSize: "1rem" }}>{j.pronto ? "✅" : "⏳"}</span>
                  </div>
                );
              })}
            </div>
            {!podeIniciar && (
              <div style={{ background: "rgba(245,200,66,0.1)", border: "1px solid rgba(245,200,66,0.3)", borderRadius: 10, padding: "10px 14px", fontSize: "0.82rem", color: "var(--gold)", textAlign: "center" }}>
                ⏳ Aguardando pelo menos 2 jogadores para iniciar ({sala.jogadores.length}/2)
              </div>
            )}
            {erro && <div style={{ color: "var(--accent)", fontSize: "0.85rem" }}>⚠️ {erro}</div>}
            <button className="btn btn-secondary btn-full" onClick={() => enviar("MARCAR_PRONTO")}>
              {sala.jogadores.find(j => j.id === jogadorId)?.pronto ? "✅ Pronto!" : "⏳ Marcar como pronto"}
            </button>
          </div>
          <div className="card-footer">
            <button className="btn btn-ghost" onClick={() => { enviar("SAIR_SALA"); onBack(); }}>← Sair</button>
            {isHost && (
              <button className="btn btn-primary" onClick={() => enviar("INICIAR_JOGO")} disabled={!podeIniciar}>
                🎮 Iniciar jogo →
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── JOGO ────────────────────────────────────────────────────────
  if (tela === "jogo") {
    const modoAtual = sala?.modoJogo;
    const meuIdx = sala ? sala.jogadores.findIndex(j => j.id === jogadorId) : -1;

    const novaRodada = () => { enviar("NOVA_RODADA"); setInstrucaoPrivada(null); setDadosJogo(null); };
    const sairSala = () => { enviar("SAIR_SALA"); onBack(); };

    return (
      <div className="app-wrapper" style={{ paddingTop: 80, paddingBottom: 40 }}>
        <div className="card" style={{ maxWidth: 540 }}>
          <div className="card-header">
            <div className="title-eyebrow">🌐 {sala?.codigo} · {MODOS.find(m => m.id === modoAtual)?.label || modoAtual}</div>
          </div>
          <div className="card-body" style={{ display: "flex", flexDirection: "column", gap: 14 }}>

            {/* Instrução privada */}
            {instrucaoPrivada && !FASES_SEM_INSTRUCAO.includes(faseJogo) && (
              <InstrucaoCard instrucao={instrucaoPrivada} />
            )}

            {/* ── Perguntas ── */}
            {faseJogo === "PERGUNTA_JOGADOR" && (
              <RespostaOnline onEnviar={(r) => enviar("ENVIAR_RESPOSTA", { resposta: r })} />
            )}
            {faseJogo === "RESULTADOS" && dadosJogo && (
              <ResultadosOnline dados={dadosJogo} isHost={isHost} onIniciarVotacao={() => enviar("INICIAR_VOTACAO")} />
            )}
            {faseJogo === "VOTACAO" && sala && (
              <VotacaoOnline jogadores={sala.jogadores} jogadorId={jogadorId}
                onVotar={(idx) => enviar("VOTAR", { votadoIdx: idx })} dadosVotos={dadosJogo} />
            )}
            {faseJogo === "PLACAR_FINAL" && dadosJogo && sala && (
              <PlacarOnline dados={dadosJogo} jogadores={sala.jogadores} isHost={isHost}
                onNovaRodada={novaRodada} onSair={sairSala} />
            )}

            {/* ── Palavra Secreta / Duas Facções ── */}
            {(faseJogo === "PALAVRA_REVEAL" || faseJogo === "FACCAO_REVEAL") && sala && (
              <RevealEspera isHost={isHost} onIniciarVotacao={() => enviar("INICIAR_VOTACAO")} />
            )}

            {/* ── Descubra a Nota ── */}
            {faseJogo === "NOTA_REVEAL" && sala && (
              <NotaRevealOnline dados={dadosJogo} isHost={isHost}
                onIniciarDiscussao={() => enviar("NOTA_INICIAR_DISCUSSAO")} />
            )}
            {faseJogo === "NOTA_DISCUSSAO" && sala && (
              <NotaDiscussaoOnline dados={dadosJogo} isHost={isHost}
                onIniciarAdivinhar={() => enviar("NOTA_INICIAR_ADIVINHAR")} />
            )}
            {faseJogo === "NOTA_ADIVINHAR" && dadosJogo && sala && (
              <NotaAdivinharOnline dados={dadosJogo} sala={sala} jogadorId={jogadorId}
                onChute={(c) => enviar("NOTA_CHUTE", { chute: c })} />
            )}
            {faseJogo === "NOTA_RESULTADO" && dadosJogo && (
              <NotaResultadoOnline dados={dadosJogo} isHost={isHost}
                onNovaRodada={novaRodada} onSair={sairSala} />
            )}

            {/* ── Adivinhe a Música ── */}
            {faseJogo === "MUSICA_BUSCAR" && (
              <MusicaBuscarOnline dados={dadosJogo} instrucaoPrivada={instrucaoPrivada}
                onSelecionarMusica={(m) => enviar("MUSICA_SELECIONADA", m)} />
            )}
            {faseJogo === "MUSICA_TOCANDO" && dadosJogo && sala && (
              <MusicaTocandoOnline dados={dadosJogo} sala={sala} jogadorId={jogadorId}
                instrucaoPrivada={instrucaoPrivada}
                onVencedor={(vid) => enviar("MUSICA_VENCEDOR", { vencedorId: vid })}
                onPalpite={(p) => enviar("MUSICA_PALPITE", { palpite: p })}
                onJulgar={(correto, vid) => enviar("MUSICA_JULGAR", { correto, vencedorId: vid })} />
            )}
            {faseJogo === "MUSICA_RESULTADO" && dadosJogo && sala && (
              <MusicaResultadoOnline dados={dadosJogo} sala={sala} isHost={isHost}
                onProximaRodada={novaRodada} onSair={sairSala} />
            )}

            {/* ── Mega Senha ── */}
            {faseJogo === "MEGASENHA_REVEAL" && sala && (
              <MegaSenhaRevealOnline dados={dadosJogo} isHost={isHost}
                onIniciarTurnos={() => enviar("MEGASENHA_INICIAR_TURNOS")} />
            )}
            {faseJogo === "MEGASENHA_TURNO" && dadosJogo && sala && (
              <MegaSenhaTurnoOnline dados={dadosJogo} sala={sala} jogadorId={jogadorId}
                meuIdx={meuIdx}
                onDica={(d) => enviar("MEGASENHA_DICA", { dica: d })}
                onAcertou={() => enviar("MEGASENHA_ACERTOU")}
                isHost={isHost}
                onNinguemAcertou={() => enviar("MEGASENHA_NINGUEM_ACERTOU")} />
            )}
            {faseJogo === "MEGASENHA_RESULTADO" && dadosJogo && sala && (
              <MegaSenhaResultadoOnline dados={dadosJogo} sala={sala} isHost={isHost}
                onNovaRodada={novaRodada} onSair={sairSala} />
            )}

          </div>
        </div>
      </div>
    );
  }

  return null;
}

// ── Instrução privada ──────────────────────────────────────────────

function InstrucaoCard({ instrucao }) {
  const { tipo, papel, nota, senha, ehImpostor, instrucao: texto, grupo, palavra } = instrucao;

  if (tipo === "nota") {
    if (papel === "dador") return (
      <div style={{ background: "rgba(245,200,66,0.12)", border: "2px solid var(--gold)", borderRadius: 14, padding: "18px 20px", textAlign: "center" }}>
        <div style={{ fontSize: "0.7rem", color: "var(--gold)", textTransform: "uppercase", marginBottom: 8 }}>🔐 Sua nota secreta</div>
        <div style={{ fontSize: "6rem", fontFamily: "'Bebas Neue'", color: "var(--gold)", lineHeight: 1 }}>{nota}</div>
        <div style={{ fontSize: "0.78rem", color: "var(--text-muted)", marginTop: 6 }}>Responda as perguntas pensando nela — sem revelar!</div>
        <div style={{ fontSize: "0.78rem", color: "var(--gold)", marginTop: 4 }}>Seu parceiro: <strong>{instrucao.parceiro}</strong></div>
      </div>
    );
    return (
      <div style={{ background: "rgba(62,170,255,0.1)", border: "2px solid #3eaaff", borderRadius: 14, padding: "16px 18px", textAlign: "center" }}>
        <div style={{ fontSize: "0.7rem", color: "#3eaaff", textTransform: "uppercase", marginBottom: 6 }}>🎯 Você é o Adivinhador</div>
        <div style={{ fontWeight: 700, fontSize: "1rem" }}>Seu Dador: <span style={{ color: "#3eaaff" }}>{instrucao.parceiro}</span></div>
        <div style={{ fontSize: "0.78rem", color: "var(--text-muted)", marginTop: 6 }}>Faça perguntas para descobrir a nota dele!</div>
      </div>
    );
  }

  if (tipo === "megasenha") {
    if (papel === "dador") return (
      <div style={{ background: "rgba(255,107,62,0.12)", border: "2px solid #ff6b3e", borderRadius: 14, padding: "18px 20px", textAlign: "center" }}>
        <div style={{ fontSize: "0.7rem", color: "#ff6b3e", textTransform: "uppercase", marginBottom: 8 }}>🔐 A senha secreta</div>
        <div style={{ fontSize: "3rem", fontFamily: "'Bebas Neue'", color: "#ff6b3e", letterSpacing: "0.06em" }}>{senha}</div>
        <div style={{ fontSize: "0.78rem", color: "var(--text-muted)", marginTop: 6 }}>Dê 1 palavra por turno como dica!</div>
        <div style={{ fontSize: "0.78rem", color: "#ff6b3e", marginTop: 4 }}>Seu parceiro: <strong>{instrucao.parceiro}</strong></div>
      </div>
    );
    return (
      <div style={{ background: "rgba(62,255,163,0.1)", border: "2px solid var(--green)", borderRadius: 14, padding: "16px 18px", textAlign: "center" }}>
        <div style={{ fontSize: "0.7rem", color: "var(--green)", textTransform: "uppercase", marginBottom: 6 }}>🎯 Você é o Adivinhador</div>
        <div style={{ fontWeight: 700 }}>Use as dicas acumuladas para descobrir a senha!</div>
      </div>
    );
  }

  if (tipo === "musica") {
    if (papel === "dj") return (
      <div style={{ background: "rgba(62,170,255,0.12)", border: "2px solid #3eaaff", borderRadius: 14, padding: "14px 18px", textAlign: "center" }}>
        <div style={{ fontSize: "0.7rem", color: "#3eaaff", textTransform: "uppercase", marginBottom: 6 }}>🎵 Você é o DJ!</div>
        <div style={{ fontWeight: 700, fontSize: "1rem" }}>Rodada {instrucao.rodada} — escolha uma música</div>
      </div>
    );
    return (
      <div style={{ background: "rgba(155,109,255,0.1)", border: "2px solid var(--purple)", borderRadius: 14, padding: "14px 18px", textAlign: "center" }}>
        <div style={{ fontSize: "0.7rem", color: "var(--purple)", textTransform: "uppercase", marginBottom: 6 }}>🎧 Rodada {instrucao.rodada}</div>
        <div style={{ fontWeight: 700 }}>DJ: <span style={{ color: "var(--purple)" }}>{instrucao.djNome}</span> escolhe a música</div>
      </div>
    );
  }

  if (tipo === "faccao") {
    const cor = grupo === "impostor" ? "var(--accent)" : grupo === "A" ? "var(--green)" : "#3eaaff";
    return (
      <div style={{ background: grupo === "impostor" ? "var(--accent-dim)" : "rgba(62,255,163,0.1)", border: `2px solid ${cor}`, borderRadius: 14, padding: "18px 20px", textAlign: "center" }}>
        <div style={{ fontSize: "0.7rem", textTransform: "uppercase", color: cor, marginBottom: 10 }}>
          {grupo === "impostor" ? "🕵️ Você é o IMPOSTOR!" : `⚔️ Grupo ${grupo}`}
        </div>
        <div style={{ fontSize: "1.6rem", fontWeight: 700, color: "var(--text-primary)" }}>"{palavra}"</div>
      </div>
    );
  }

  return (
    <div style={{ background: ehImpostor ? "var(--accent-dim)" : "rgba(62,255,163,0.1)", border: `2px solid ${ehImpostor ? "var(--accent)" : "var(--green)"}`, borderRadius: 14, padding: "18px 20px", textAlign: "center" }}>
      <div style={{ fontSize: "0.7rem", textTransform: "uppercase", color: ehImpostor ? "var(--accent)" : "var(--green)", marginBottom: 10 }}>
        {ehImpostor ? "🕵️ Você é o IMPOSTOR!" : "👤 Sua instrução"}
      </div>
      <div style={{ fontSize: "1.2rem", fontWeight: 700, color: "var(--text-primary)", lineHeight: 1.5 }}>"{texto}"</div>
    </div>
  );
}

// ── Palavra Secreta / Duas Facções ─────────────────────────────────

function RevealEspera({ isHost, onIniciarVotacao }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ textAlign: "center", padding: "20px 0", color: "var(--text-secondary)" }}>
        🔐 Cada jogador recebeu sua instrução privada. Discutam livremente!
      </div>
      {isHost
        ? <button className="btn btn-primary btn-full" onClick={onIniciarVotacao}>🗳️ Iniciar votação →</button>
        : <div style={{ textAlign: "center", color: "var(--text-muted)", fontSize: "0.85rem" }}>⏳ Aguardando o host iniciar a votação...</div>
      }
    </div>
  );
}

// ── Descubra a Nota ────────────────────────────────────────────────

function NotaRevealOnline({ dados, isHost, onIniciarDiscussao }) {
  const pares = dados?.pares || [];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {pares.length > 0 && (
        <div style={{ background: "var(--bg-elevated)", borderRadius: 12, padding: "14px 16px" }}>
          <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", textTransform: "uppercase", marginBottom: 10 }}>👥 Duplas desta rodada</div>
          {pares.map((p, i) => {
            const avD = avStyle(p.dador.idx);
            const avA = avStyle(p.adivinhador.idx);
            return (
              <div key={i} style={{ background: "var(--bg-elevated)", borderRadius: 14, padding: "12px 14px", border: "1px solid var(--border)", marginBottom: 8 }}>
                <div style={{ fontSize: "0.7rem", color: "var(--gold)", fontWeight: 700, textTransform: "uppercase", marginBottom: 8 }}>Dupla {i + 1}</div>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: "0.7rem", color: "var(--text-muted)", marginBottom: 4 }}>🎙️ Dador</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ width: 36, height: 36, borderRadius: "50%", background: avD.bg, border: `2px solid ${avD.border}`, color: avD.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.2rem" }}>{p.dador.avatar}</div>
                      <span style={{ fontWeight: 700, color: avD.color }}>{p.dador.nome}</span>
                    </div>
                  </div>
                  <span style={{ color: "var(--text-muted)", fontSize: "1.2rem" }}>↔️</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: "0.7rem", color: "var(--text-muted)", marginBottom: 4 }}>🎯 Adivinhador</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ width: 36, height: 36, borderRadius: "50%", background: avA.bg, border: `2px solid ${avA.border}`, color: avA.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.2rem" }}>{p.adivinhador.avatar}</div>
                      <span style={{ fontWeight: 700, color: avA.color }}>{p.adivinhador.nome}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div style={{ background: "rgba(245,200,66,0.08)", border: "1px solid rgba(245,200,66,0.3)", borderRadius: 10, padding: "12px 16px", fontSize: "0.82rem", color: "var(--text-secondary)" }}>
        💡 Dadores: <strong style={{ color: "var(--gold)" }}>vejam sua nota acima.</strong> Adivinhadores: aguardem!
      </div>
      {isHost
        ? <button className="btn btn-primary btn-full" onClick={onIniciarDiscussao}>🎤 Todos viram — iniciar perguntas →</button>
        : <div style={{ textAlign: "center", color: "var(--text-muted)", fontSize: "0.85rem" }}>⏳ Aguardando o host iniciar...</div>
      }
    </div>
  );
}

function NotaDiscussaoOnline({ dados, isHost, onIniciarAdivinhar }) {
  const pares = dados?.pares || [];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div className="title-eyebrow" style={{ textAlign: "center" }}>🎯 Descubra a Nota · Perguntas</div>

      {pares.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {pares.map((p, i) => {
            const avD = avStyle(p.dador.idx);
            const avA = avStyle(p.adivinhador.idx);
            return (
              <div key={i} style={{ background: "var(--bg-elevated)", borderRadius: 12, padding: "12px 14px", border: "1px solid var(--border)" }}>
                <div style={{ fontSize: "0.7rem", color: "var(--gold)", fontWeight: 700, textTransform: "uppercase", marginBottom: 8 }}>Dupla {i + 1}</div>
                <div style={{ display: "flex", alignItems: "center", gap: 0 }}>
                  <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ width: 32, height: 32, borderRadius: "50%", background: avA.bg, border: `2px solid ${avA.border}`, color: avA.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1rem" }}>{p.adivinhador.avatar}</div>
                    <div>
                      <div style={{ fontWeight: 700, color: avA.color, fontSize: "0.88rem" }}>{p.adivinhador.nome}</div>
                      <div style={{ color: "var(--text-muted)", fontSize: "0.68rem" }}>faz perguntas ➜</div>
                    </div>
                  </div>
                  <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 8, justifyContent: "flex-end" }}>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontWeight: 700, color: avD.color, fontSize: "0.88rem" }}>{p.dador.nome}</div>
                      <div style={{ color: "var(--text-muted)", fontSize: "0.68rem" }}>responde com dicas</div>
                    </div>
                    <div style={{ width: 32, height: 32, borderRadius: "50%", background: avD.bg, border: `2px solid ${avD.border}`, color: avD.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1rem" }}>{p.dador.avatar}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div style={{ background: "rgba(245,200,66,0.1)", border: "1px solid var(--gold)", borderRadius: 12, padding: "14px 18px" }}>
        <div style={{ fontSize: "0.75rem", color: "var(--gold)", fontWeight: 700, textTransform: "uppercase", marginBottom: 8 }}>💡 Perguntas sugeridas</div>
        <div style={{ fontSize: "0.84rem", color: "var(--text-secondary)", lineHeight: 1.8 }}>
          • "Qual lugar tem essa nota?"<br />
          • "Qual comida tem essa nota?"<br />
          • "Que celebridade tem essa nota?"<br />
          • "Que experiência tem essa nota?"
        </div>
      </div>

      {isHost
        ? <button className="btn btn-primary btn-full" onClick={onIniciarAdivinhar}>🎯 Hora de adivinhar →</button>
        : <div style={{ textAlign: "center", color: "var(--text-muted)", fontSize: "0.85rem" }}>⏳ Aguardando o host avançar...</div>
      }
    </div>
  );
}

function NotaAdivinharOnline({ dados, sala, jogadorId, onChute }) {
  const [chute, setChute] = useState(null);
  const [enviado, setEnviado] = useState(false);
  const adivAtual = dados.adivinhadorAtual;
  const meuIdx = sala.jogadores.findIndex(j => j.id === jogadorId);
  const meuTurno = adivAtual && adivAtual.idx === meuIdx;
  const ultimoChute = dados.ultimoChute;

  const par = dados.pares?.find(p => p.adivinhador.idx === adivAtual?.idx);
  const dadorDoAdiv = par?.dador;

  useEffect(() => {
    if (ultimoChute) { setEnviado(false); setChute(null); }
  }, [ultimoChute?.adivinhadorIdx]);

  const avA = adivAtual ? avStyle(adivAtual.idx) : null;
  const avD = dadorDoAdiv ? avStyle(dadorDoAdiv.idx) : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div className="title-eyebrow" style={{ textAlign: "center" }}>🎯 Descubra a Nota · Adivinhação</div>

      {/* Resultado do último chute */}
      {ultimoChute && (
        <div style={{ background: ultimoChute.acertou ? "rgba(62,255,163,0.12)" : "rgba(255,62,62,0.12)", border: `2px solid ${ultimoChute.acertou ? "var(--green)" : "var(--accent)"}`, borderRadius: 12, padding: "12px 16px", textAlign: "center" }}>
          <div style={{ fontWeight: 700, color: ultimoChute.acertou ? "var(--green)" : "var(--accent)", fontSize: "1rem" }}>
            {ultimoChute.adivinhadorNome}: {ultimoChute.acertou ? "✅ Acertou!" : `❌ Chutou ${ultimoChute.chute}`}
          </div>
        </div>
      )}

      {/* Par atual: adivinhador → dador */}
      {adivAtual && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 20, padding: "8px 0" }}>
          <div style={{ textAlign: "center" }}>
            <div style={{ width: 56, height: 56, borderRadius: "50%", background: avA?.bg, border: `3px solid ${avA?.border}`, color: avA?.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.8rem", margin: "0 auto 6px", boxShadow: `0 0 20px ${avA?.border}44` }}>
              {sala.jogadores[adivAtual.idx]?.avatar}
            </div>
            <div style={{ fontWeight: 700, color: avA?.color, fontSize: "0.88rem" }}>{adivAtual.nome}</div>
            <div style={{ color: "var(--text-muted)", fontSize: "0.7rem" }}>Adivinhador</div>
          </div>
          <div style={{ fontSize: "1.4rem", color: "var(--text-muted)" }}>→</div>
          {dadorDoAdiv && (
            <div style={{ textAlign: "center" }}>
              <div style={{ width: 56, height: 56, borderRadius: "50%", background: avD?.bg, border: `3px solid ${avD?.border}`, color: avD?.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.8rem", margin: "0 auto 6px", boxShadow: `0 0 20px ${avD?.border}44` }}>
                {sala.jogadores[dadorDoAdiv.idx]?.avatar}
              </div>
              <div style={{ fontWeight: 700, color: avD?.color, fontSize: "0.88rem" }}>{dadorDoAdiv.nome}</div>
              <div style={{ color: "var(--text-muted)", fontSize: "0.7rem" }}>Dador</div>
            </div>
          )}
        </div>
      )}

      {/* Grade de números */}
      {meuTurno && !enviado ? (
        <>
          <div style={{ textAlign: "center", fontSize: "0.84rem", color: "var(--text-secondary)", marginBottom: 4 }}>
            {adivAtual.nome}, qual é a nota?
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10 }}>
            {[1,2,3,4,5,6,7,8,9,10].map(n => (
              <button key={n} onClick={() => setChute(n)} style={{
                aspectRatio: "1", borderRadius: 12,
                border: `2px solid ${chute === n ? "var(--gold)" : "var(--border)"}`,
                background: chute === n ? "rgba(245,200,66,0.2)" : "var(--bg-elevated)",
                color: chute === n ? "var(--gold)" : "var(--text-primary)",
                fontFamily: "'Bebas Neue'", fontSize: "1.6rem", cursor: "pointer",
                boxShadow: chute === n ? "0 0 16px rgba(245,200,66,0.3)" : "none",
              }}>{n}</button>
            ))}
          </div>
          <button className="btn btn-primary btn-full" disabled={chute === null}
            onClick={() => { onChute(chute); setEnviado(true); }}>
            🎯 Confirmar: Nota {chute ?? "?"}
          </button>
        </>
      ) : meuTurno && enviado ? (
        <div style={{ textAlign: "center", padding: 20 }}>
          <div style={{ fontSize: "2rem", marginBottom: 8 }}>✅</div>
          <div style={{ color: "var(--green)", fontWeight: 700 }}>Chute enviado!</div>
          <div style={{ color: "var(--text-muted)", fontSize: "0.85rem", marginTop: 4 }}>Aguardando os outros...</div>
        </div>
      ) : (
        <div style={{ textAlign: "center", padding: 20, color: "var(--text-muted)", fontSize: "0.88rem" }}>
          ⏳ Vez de <strong style={{ color: "var(--text-primary)" }}>{adivAtual?.nome}</strong> adivinhar...
        </div>
      )}
    </div>
  );
}

function NotaResultadoOnline({ dados, isHost, onNovaRodada, onSair }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {/* Nota revelada em destaque */}
      <div style={{ textAlign: "center", padding: "16px 0" }}>
        <div style={{ fontSize: "0.72rem", color: "var(--gold)", textTransform: "uppercase", marginBottom: 8 }}>🔐 A nota era</div>
        <div style={{ fontSize: "7rem", fontFamily: "'Bebas Neue'", color: "var(--gold)", lineHeight: 1 }}>{dados.nota}</div>
        <div style={{ fontSize: "0.82rem", color: "var(--text-muted)", marginTop: 4 }}>de 10</div>
      </div>

      {/* Resultado de cada adivinhador */}
      <div style={{ background: "var(--bg-elevated)", borderRadius: 12, padding: "14px 16px" }}>
        <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", textTransform: "uppercase", marginBottom: 10 }}>📊 Resultado</div>
        {(dados.chutes || []).map((c, i) => {
          const av = avStyle(c.adivinhadorIdx);
          return (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: "1px solid var(--border)" }}>
              <div style={{ width: 36, height: 36, borderRadius: "50%", background: av.bg, border: `2px solid ${av.border}`, color: av.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.1rem" }}>
                {c.adivinhadorAvatar || av.emoji}
              </div>
              <span style={{ flex: 1, fontWeight: 700, color: av.color }}>{c.adivinhadorNome}</span>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <div style={{ background: "rgba(245,200,66,0.12)", border: "1px solid var(--gold)", borderRadius: 8, padding: "4px 10px", fontFamily: "'Bebas Neue'", fontSize: "1.2rem", color: "var(--gold)" }}>
                  {c.chute}
                </div>
                <span style={{ color: c.acertou ? "var(--green)" : "var(--accent)", fontSize: "1.2rem" }}>{c.acertou ? "✅" : "❌"}</span>
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ display: "flex", gap: 10 }}>
        <button className="btn btn-secondary" style={{ flex: 1 }} onClick={onSair}>🏠 Sair</button>
        {isHost && <button className="btn btn-primary" style={{ flex: 1 }} onClick={onNovaRodada}>🔄 Nova rodada</button>}
        {!isHost && <div style={{ flex: 1, textAlign: "center", color: "var(--text-muted)", fontSize: "0.82rem", padding: "12px 0" }}>Aguardando host...</div>}
      </div>
    </div>
  );
}

// ── Adivinhe a Música ──────────────────────────────────────────────

function BuscaMusica({ onSelecionada }) {
  const [query, setQuery] = useState("");
  const [resultados, setResultados] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState("");
  const [buscou, setBuscou] = useState(false);

  const buscar = async () => {
    if (!query.trim()) return;
    setCarregando(true); setErro(""); setBuscou(true);
    try {
      const res = await fetch(`${API_BASE}/api/buscar-musica?q=${encodeURIComponent(query.trim())}`);
      const data = await res.json();
      if (data.erro) { setErro(data.erro); setResultados([]); }
      else {
        setResultados(data.resultados || []);
        if ((data.resultados || []).length === 0) setErro("Nenhuma música encontrada.");
      }
    } catch { setErro("Erro ao conectar com o servidor."); }
    setCarregando(false);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div className="input-group" style={{ marginBottom: 0 }}>
        <label className="input-label">🔍 Buscar música</label>
        <div style={{ display: "flex", gap: 8 }}>
          <input className="input-field" style={{ flex: 1, marginBottom: 0 }}
            placeholder="Ex: Bohemian Rhapsody, funk, sertanejo..."
            value={query} onChange={e => setQuery(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") buscar(); }} autoFocus />
          <button className="btn btn-primary" onClick={buscar} disabled={carregando || !query.trim()} style={{ padding: "0 20px", whiteSpace: "nowrap" }}>
            {carregando ? "..." : "🔍"}
          </button>
        </div>
      </div>
      {erro && (
        <div style={{ background: "var(--accent-dim)", border: "1px solid var(--accent)", borderRadius: 10, padding: "10px 14px", fontSize: "0.84rem", color: "var(--accent)" }}>
          ⚠️ {erro}
        </div>
      )}
      {carregando && (
        <div style={{ textAlign: "center", padding: "20px", color: "var(--text-muted)", fontSize: "0.88rem" }}>🔍 Buscando músicas...</div>
      )}
      {!carregando && resultados.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", textTransform: "uppercase" }}>
            {resultados.length} resultado{resultados.length !== 1 ? "s" : ""}
          </div>
          {resultados.map(r => (
            <button key={r.videoId} onClick={() => onSelecionada(r)} style={{
              display: "flex", alignItems: "center", gap: 12,
              background: "var(--bg-elevated)", border: "1px solid var(--border)",
              borderRadius: 12, padding: "10px 12px", cursor: "pointer", textAlign: "left",
              transition: "border-color 0.15s",
            }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = "#3eaaff"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--border)"; }}>
              {r.thumb && <img src={r.thumb} alt="" style={{ width: 64, height: 48, borderRadius: 8, objectFit: "cover", flexShrink: 0 }} />}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: "0.88rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.titulo}</div>
                <div style={{ fontSize: "0.75rem", color: "var(--text-secondary)", marginTop: 2 }}>{r.canal}</div>
              </div>
              <div style={{ color: "#3eaaff", fontSize: "1.2rem", flexShrink: 0 }}>▶</div>
            </button>
          ))}
        </div>
      )}
      {!carregando && buscou && resultados.length === 0 && !erro && (
        <div style={{ textAlign: "center", color: "var(--text-muted)", fontSize: "0.85rem" }}>Nenhum resultado encontrado.</div>
      )}
    </div>
  );
}

function MusicaBuscarOnline({ dados, instrucaoPrivada, onSelecionarMusica }) {
  const isDJ = instrucaoPrivada?.papel === "dj";
  const [tocarEmTodos, setTocarEmTodos] = useState(false);
  const djNome = dados?.djNome || instrucaoPrivada?.djNome || "";
  const rodada = dados?.rodada || instrucaoPrivada?.rodada || 1;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ background: "rgba(62,170,255,0.1)", border: "1px solid #3eaaff40", borderRadius: 12, padding: "12px 16px" }}>
        <div style={{ fontSize: "0.72rem", color: "#3eaaff", textTransform: "uppercase", marginBottom: 4 }}>🎵 Rodada {rodada}</div>
        <div style={{ fontWeight: 700 }}>DJ: <span style={{ color: "#3eaaff" }}>{djNome}</span></div>
      </div>

      {isDJ ? (
        <>
          <button onClick={() => setTocarEmTodos(v => !v)} style={{
            display: "flex", alignItems: "center", gap: 12,
            background: tocarEmTodos ? "rgba(62,170,255,0.15)" : "var(--bg-elevated)",
            border: `2px solid ${tocarEmTodos ? "#3eaaff" : "var(--border)"}`,
            borderRadius: 12, padding: "12px 16px", cursor: "pointer", textAlign: "left",
          }}>
            <span style={{ fontSize: "1.4rem" }}>{tocarEmTodos ? "📱" : "🔊"}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, color: tocarEmTodos ? "#3eaaff" : "var(--text-primary)", fontSize: "0.9rem" }}>
                {tocarEmTodos ? "Tocar em todos os celulares" : "Tocar só no celular do DJ"}
              </div>
              <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginTop: 2 }}>
                {tocarEmTodos ? "Cada um ouve no próprio dispositivo" : "Toque em voz alta para todos ouvirem"}
              </div>
            </div>
            <div style={{ width: 44, height: 24, borderRadius: 12, background: tocarEmTodos ? "#3eaaff" : "var(--border)", position: "relative", flexShrink: 0 }}>
              <div style={{ position: "absolute", top: 3, left: tocarEmTodos ? 23 : 3, width: 18, height: 18, borderRadius: "50%", background: "#fff", transition: "left 0.2s" }} />
            </div>
          </button>
          <BuscaMusica onSelecionada={(m) => onSelecionarMusica({ ...m, tocarEmTodos })} />
        </>
      ) : (
        <div style={{ textAlign: "center", padding: "30px 0", color: "var(--text-muted)" }}>
          <div style={{ fontSize: "2.5rem", marginBottom: 12 }}>⏳</div>
          <div style={{ fontWeight: 700, color: "var(--text-primary)" }}>{djNome} está escolhendo a música...</div>
          <div style={{ fontSize: "0.82rem", marginTop: 6 }}>Prepare-se para adivinhar!</div>
        </div>
      )}
    </div>
  );
}

function MusicaTocandoOnline({ dados, sala, jogadorId, instrucaoPrivada, onVencedor, onPalpite, onJulgar }) {
  const [selecionado, setSelecionado] = useState(null);
  const [ninguem, setNinguem] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const [chutando, setChutando] = useState(false);
  const [textoPalpite, setTextoPalpite] = useState("");
  const [aguardandoJulgamento, setAguardandoJulgamento] = useState(false);

  const isDJ = instrucaoPrivada?.papel === "dj";
  const tocarEmTodos = dados.musica?.tocarEmTodos;
  const palpitePendente = dados.palpitePendente;
  const meuPalpiteErrado = dados.ultimoPalpiteErrado?.jogadorId === jogadorId;

  useEffect(() => {
    if (meuPalpiteErrado) { setChutando(false); setTextoPalpite(""); setAguardandoJulgamento(false); }
  }, [dados.ultimoPalpiteErrado?.jogadorId]);

  const enviarPalpite = () => {
    if (!textoPalpite.trim()) return;
    onPalpite(textoPalpite.trim());
    setAguardandoJulgamento(true);
    setChutando(false);
  };

  const ouvintes = sala.jogadores.filter((_, idx) => idx !== dados.djIdx);

  // ── Modo físico: DJ toca em voz alta ─────────────────────────────
  if (!tocarEmTodos) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={{ background: "rgba(62,170,255,0.08)", border: "1px solid #3eaaff30", borderRadius: 12, padding: "16px", textAlign: "center" }}>
          <div style={{ fontSize: "1.8rem", marginBottom: 6 }}>🔊</div>
          <div style={{ fontWeight: 700, color: "#3eaaff" }}>{dados.djNome} está tocando a música</div>
          <div style={{ fontSize: "0.82rem", color: "var(--text-muted)", marginTop: 4 }}>Grite o nome quando souber!</div>
        </div>
        {isDJ && !enviado && (
          <>
            <div style={{ fontWeight: 700, color: "var(--text-secondary)", fontSize: "0.88rem" }}>🏆 Quem acertou primeiro?</div>
            {ouvintes.map((j) => {
              const idx = sala.jogadores.indexOf(j);
              const av = avStyle(idx);
              const sel = selecionado === j.id;
              return (
                <button key={j.id} onClick={() => { setSelecionado(j.id); setNinguem(false); }} style={{
                  display: "flex", alignItems: "center", gap: 12,
                  background: sel ? av.bg : "var(--bg-elevated)",
                  border: `2px solid ${sel ? av.border : "var(--border)"}`,
                  borderRadius: 12, padding: "12px 16px", cursor: "pointer",
                  boxShadow: sel ? `0 0 16px ${av.border}40` : "none",
                }}>
                  <div style={{ width: 40, height: 40, borderRadius: "50%", background: av.bg, border: `2px solid ${av.border}`, color: av.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.3rem" }}>{j.avatar}</div>
                  <span style={{ fontWeight: 700, flex: 1, color: sel ? av.color : "var(--text-primary)" }}>{j.nome}</span>
                  {sel && <span style={{ color: av.color, fontSize: "1.2rem" }}>⭐</span>}
                </button>
              );
            })}
            <button onClick={() => { setNinguem(true); setSelecionado(null); }} style={{
              background: ninguem ? "rgba(255,62,62,0.15)" : "var(--bg-elevated)",
              border: `2px solid ${ninguem ? "var(--accent)" : "var(--border)"}`,
              borderRadius: 12, padding: "14px 16px", cursor: "pointer",
              color: ninguem ? "var(--accent)" : "var(--text-secondary)", fontWeight: 700,
            }}>😞 Ninguém acertou</button>
            <button className="btn btn-primary btn-full" disabled={!selecionado && !ninguem}
              onClick={() => { onVencedor(selecionado); setEnviado(true); }}>
              Confirmar →
            </button>
          </>
        )}
        {isDJ && enviado && (
          <div style={{ textAlign: "center", padding: 16, color: "var(--green)", fontWeight: 700 }}>✅ Resultado enviado!</div>
        )}
        {!isDJ && (
          <div style={{ background: "rgba(62,170,255,0.08)", border: "1px solid #3eaaff30", borderRadius: 10, padding: "12px 14px", fontSize: "0.84rem", color: "var(--text-secondary)", textAlign: "center" }}>
            🎧 Ouça a música e grite o nome quando souber!
          </div>
        )}
      </div>
    );
  }

  // ── Modo remoto: toca em todos os celulares ───────────────────────
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

      {dados.musica && !chutando && !aguardandoJulgamento && (
        <div style={{ position: "relative", paddingBottom: "56.25%", height: 0, borderRadius: 12, overflow: "hidden", border: "2px solid #3eaaff40" }}>
          <iframe
            src={`https://www.youtube.com/embed/${dados.musica.videoId}?autoplay=1&rel=0&controls=0&modestbranding=1&showinfo=0`}
            title="Música"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: "none" }}
          />
        </div>
      )}

      {meuPalpiteErrado && !isDJ && (
        <div style={{ background: "rgba(255,62,62,0.15)", border: "2px solid var(--accent)", borderRadius: 12, padding: "14px", textAlign: "center" }}>
          <div style={{ fontSize: "1.6rem", marginBottom: 4 }}>❌</div>
          <div style={{ fontWeight: 700, color: "var(--accent)" }}>Errado! Continue ouvindo...</div>
        </div>
      )}

      {/* Ouvinte */}
      {!isDJ && (
        <>
          {aguardandoJulgamento && (
            <div style={{ background: "rgba(245,200,66,0.12)", border: "2px solid var(--gold)", borderRadius: 12, padding: "16px", textAlign: "center" }}>
              <div style={{ fontSize: "1.4rem", marginBottom: 6 }}>⏳</div>
              <div style={{ fontWeight: 700, color: "var(--gold)" }}>Aguardando o DJ julgar...</div>
            </div>
          )}
          {chutando && !aguardandoJulgamento && (
            <div style={{ background: "rgba(62,170,255,0.1)", border: "2px solid #3eaaff", borderRadius: 14, padding: "16px", display: "flex", flexDirection: "column", gap: 10 }}>
              <div style={{ fontSize: "0.72rem", color: "#3eaaff", textTransform: "uppercase", fontWeight: 700 }}>🎯 Qual é a música?</div>
              <input className="input-field" placeholder="Nome da música ou artista..."
                value={textoPalpite} onChange={e => setTextoPalpite(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") enviarPalpite(); }}
                style={{ marginBottom: 0 }} autoFocus />
              <div style={{ display: "flex", gap: 8 }}>
                <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => { setChutando(false); setTextoPalpite(""); }}>← Voltar</button>
                <button className="btn btn-primary" style={{ flex: 1 }} disabled={!textoPalpite.trim()} onClick={enviarPalpite}>✅ Enviar</button>
              </div>
            </div>
          )}
          {!chutando && !aguardandoJulgamento && !palpitePendente && (
            <button onClick={() => setChutando(true)} style={{
              background: "rgba(62,255,163,0.15)", border: "2px solid var(--green)",
              borderRadius: 12, padding: "16px", cursor: "pointer",
              color: "var(--green)", fontWeight: 700, fontSize: "1rem",
            }}>
              🛑 Para a música — Eu sei!
            </button>
          )}
          {!chutando && !aguardandoJulgamento && palpitePendente && palpitePendente.jogadorId !== jogadorId && (
            <div style={{ background: "var(--bg-elevated)", border: "1px solid var(--border)", borderRadius: 12, padding: "14px", textAlign: "center" }}>
              <div style={{ color: "var(--text-muted)", fontSize: "0.85rem" }}>
                ⏳ <strong style={{ color: "var(--text-primary)" }}>{palpitePendente.jogadorNome}</strong> está chutando...
              </div>
            </div>
          )}
        </>
      )}

      {/* DJ */}
      {isDJ && (
        <>
          {palpitePendente ? (
            <div style={{ background: "rgba(245,200,66,0.12)", border: "2px solid var(--gold)", borderRadius: 14, padding: "18px 20px" }}>
              <div style={{ fontSize: "0.72rem", color: "var(--gold)", textTransform: "uppercase", marginBottom: 10 }}>🎯 Palpite recebido!</div>
              <div style={{ fontWeight: 700, fontSize: "1rem", marginBottom: 4 }}>{palpitePendente.jogadorNome} disse:</div>
              <div style={{ fontSize: "1.6rem", fontFamily: "'Bebas Neue'", color: "var(--gold)", marginBottom: 16 }}>"{palpitePendente.palpite}"</div>
              <div style={{ display: "flex", gap: 10 }}>
                <button style={{ flex: 1, background: "rgba(255,62,62,0.2)", border: "2px solid var(--accent)", borderRadius: 10, padding: "12px", cursor: "pointer", color: "var(--accent)", fontWeight: 700, fontSize: "1rem" }}
                  onClick={() => onJulgar(false, null)}>❌ Errado</button>
                <button style={{ flex: 1, background: "rgba(62,255,163,0.2)", border: "2px solid var(--green)", borderRadius: 10, padding: "12px", cursor: "pointer", color: "var(--green)", fontWeight: 700, fontSize: "1rem" }}
                  onClick={() => onJulgar(true, palpitePendente.jogadorId)}>✅ Correto!</button>
              </div>
            </div>
          ) : (
            <div style={{ background: "rgba(62,170,255,0.08)", border: "1px solid #3eaaff30", borderRadius: 10, padding: "12px 16px", fontSize: "0.84rem", color: "var(--text-secondary)", textAlign: "center" }}>
              🎧 Aguardando alguém chutar a música...
            </div>
          )}
          {!palpitePendente && (
            <button onClick={() => onVencedor(null)} style={{
              background: "var(--bg-elevated)", border: "1px solid var(--border)",
              borderRadius: 10, padding: "10px", cursor: "pointer",
              color: "var(--text-muted)", fontSize: "0.82rem",
            }}>⏭️ Ninguém acertou — próxima</button>
          )}
        </>
      )}
    </div>
  );
}

function MusicaResultadoOnline({ dados, sala, isHost, onProximaRodada, onSair }) {
  const placarArr = sala.jogadores
    .map((j, idx) => ({ nome: j.nome, avatar: j.avatar, pts: dados.placar?.[idx] || 0, idx }))
    .sort((a, b) => b.pts - a.pts);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ textAlign: "center", padding: "8px 0" }}>
        {dados.vencedorNome
          ? <><div style={{ fontSize: "3rem", marginBottom: 8 }}>🏆</div><div style={{ fontFamily: "'Bebas Neue'", fontSize: "1.8rem", color: "#3eaaff" }}>{dados.vencedorNome} acertou!</div></>
          : <><div style={{ fontSize: "3rem", marginBottom: 8 }}>😞</div><div style={{ fontFamily: "'Bebas Neue'", fontSize: "1.8rem", color: "var(--text-secondary)" }}>Ninguém acertou</div></>
        }
        {dados.musica?.titulo && <div style={{ color: "var(--text-muted)", fontSize: "0.82rem", marginTop: 6 }}>🎵 {dados.musica.titulo}</div>}
      </div>

      <div style={{ background: "var(--bg-elevated)", borderRadius: 12, padding: "14px 16px" }}>
        <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", textTransform: "uppercase", marginBottom: 10 }}>🏆 Placar · Rodada {dados.rodada}</div>
        {placarArr.map(({ nome, avatar, pts, idx }, pos) => {
          const av = avStyle(idx);
          return (
            <div key={idx} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: "1px solid var(--border)", background: pos === 0 && pts > 0 ? "rgba(245,200,66,0.06)" : "transparent", borderRadius: pos === 0 ? 8 : 0 }}>
              <div style={{ width: 28, textAlign: "center", fontSize: "1rem" }}>{pos === 0 && pts > 0 ? "👑" : `${pos + 1}°`}</div>
              <div style={{ width: 36, height: 36, borderRadius: "50%", background: av.bg, border: `2px solid ${av.border}`, color: av.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.1rem" }}>{avatar}</div>
              <span style={{ flex: 1, fontWeight: 700 }}>{nome}</span>
              <span style={{ fontFamily: "'Bebas Neue'", fontSize: "1.6rem", color: pos === 0 && pts > 0 ? "var(--gold)" : "#3eaaff" }}>{pts} pt{pts !== 1 ? "s" : ""}</span>
            </div>
          );
        })}
      </div>

      <div style={{ display: "flex", gap: 10 }}>
        <button className="btn btn-secondary" style={{ flex: 1 }} onClick={onSair}>🏠 Sair</button>
        {isHost && <button className="btn btn-primary" style={{ flex: 1 }} onClick={onProximaRodada}>🎵 Próxima →</button>}
        {!isHost && <div style={{ flex: 1, textAlign: "center", color: "var(--text-muted)", fontSize: "0.82rem", padding: "12px 0" }}>Aguardando host...</div>}
      </div>
    </div>
  );
}

// ── Mega Senha ─────────────────────────────────────────────────────

function MegaSenhaRevealOnline({ dados, isHost, onIniciarTurnos }) {
  const pares = dados?.pares || [];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div className="title-eyebrow" style={{ textAlign: "center" }}>🏆 Mega Senha · Revelação</div>

      {pares.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {pares.map((p, i) => {
            const avD = avStyle(p.dador.idx);
            const avA = avStyle(p.adivinhador.idx);
            return (
              <div key={i} style={{ background: "var(--bg-elevated)", borderRadius: 14, padding: "12px 14px", border: "1px solid var(--border)" }}>
                <div style={{ fontSize: "0.7rem", color: "#ff6b3e", fontWeight: 700, textTransform: "uppercase", marginBottom: 8 }}>Dupla {i + 1}</div>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: "0.7rem", color: "var(--text-muted)", marginBottom: 4 }}>🎙️ Dador</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ width: 36, height: 36, borderRadius: "50%", background: avD.bg, border: `2px solid ${avD.border}`, color: avD.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.2rem" }}>{p.dador.avatar}</div>
                      <span style={{ fontWeight: 700, color: avD.color }}>{p.dador.nome}</span>
                    </div>
                  </div>
                  <span style={{ color: "var(--text-muted)" }}>↔️</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: "0.7rem", color: "var(--text-muted)", marginBottom: 4 }}>🎯 Adivinhador</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ width: 36, height: 36, borderRadius: "50%", background: avA.bg, border: `2px solid ${avA.border}`, color: avA.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.2rem" }}>{p.adivinhador.avatar}</div>
                      <span style={{ fontWeight: 700, color: avA.color }}>{p.adivinhador.nome}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div style={{ background: "rgba(255,107,62,0.08)", border: "1px solid rgba(255,107,62,0.3)", borderRadius: 10, padding: "12px 16px", fontSize: "0.82rem", color: "var(--text-secondary)" }}>
        🔐 Dadores: <strong style={{ color: "#ff6b3e" }}>vejam a senha secreta acima.</strong> Adivinhadores: não olhem!
      </div>

      {isHost
        ? <button className="btn btn-primary btn-full" onClick={onIniciarTurnos}>🏆 Todos viram — iniciar turnos →</button>
        : <div style={{ textAlign: "center", color: "var(--text-muted)", fontSize: "0.85rem" }}>⏳ Aguardando o host iniciar...</div>
      }
    </div>
  );
}

function MegaSenhaTurnoOnline({ dados, sala, jogadorId, meuIdx, onDica, onAcertou, isHost, onNinguemAcertou }) {
  const [dica, setDica] = useState("");
  const [dicaEnviada, setDicaEnviada] = useState(false);
  const [erroDica, setErroDica] = useState("");

  const dadorAtual = dados.dadorAtual;
  const ehMeuTurno = dadorAtual && dadorAtual.idx === meuIdx;
  const souAdivinhador = dados.pares && dados.pares.some(p => p.adivinhador.idx === meuIdx);
  const avD = dadorAtual ? avStyle(dadorAtual.idx) : null;

  const confirmarDica = () => {
    const t = dica.trim();
    if (!t) { setErroDica("Digite uma dica!"); return; }
    if (t.split(/\s+/).length > 1) { setErroDica("Apenas UMA palavra!"); return; }
    onDica(t);
    setDica(""); setErroDica(""); setDicaEnviada(true);
    setTimeout(() => setDicaEnviada(false), 2000);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div className="title-eyebrow" style={{ textAlign: "center" }}>🏆 Mega Senha · Turno {(dados.dicas || []).length + 1}</div>

      {/* Dicas acumuladas */}
      {(dados.dicas || []).length > 0 && (
        <div style={{ background: "var(--bg-elevated)", borderRadius: 12, padding: "14px 16px" }}>
          <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", textTransform: "uppercase", marginBottom: 10 }}>
            💬 Dicas acumuladas ({dados.dicas.length})
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {dados.dicas.map((d, i) => {
              const av = avStyle(d.dadorIdx);
              const nomeDador = sala.jogadores[d.dadorIdx]?.nome?.split(" ")[0] || "";
              return (
                <span key={i} style={{ background: av.bg, border: `1px solid ${av.border}`, color: av.color, borderRadius: 20, padding: "5px 14px", fontSize: "0.88rem", fontWeight: 700 }}>
                  {d.dica} <span style={{ fontSize: "0.65rem", opacity: 0.7 }}>{nomeDador}</span>
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Dador atual */}
      {dadorAtual && (
        <div style={{ background: "#ff6b3e15", border: `2px solid ${ehMeuTurno ? "#ff6b3e" : "var(--border)"}`, borderRadius: 14, padding: "16px" }}>
          <div style={{ fontSize: "0.72rem", color: "#ff6b3e", textTransform: "uppercase", fontWeight: 700, marginBottom: 10 }}>🎙️ Vez de dar dica</div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: ehMeuTurno ? 14 : 0 }}>
            <div style={{ width: 44, height: 44, borderRadius: "50%", background: avD?.bg, border: `2px solid ${avD?.border}`, color: avD?.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.4rem" }}>
              {dadorAtual.avatar || avD?.emoji}
            </div>
            <div>
              <div style={{ fontWeight: 700, color: "#ff6b3e" }}>{dadorAtual.nome}</div>
              <div style={{ fontSize: "0.72rem", color: "var(--text-muted)" }}>Dador de Dicas</div>
            </div>
          </div>
          {ehMeuTurno ? (
            <>
              <div className="input-group" style={{ marginBottom: 0 }}>
                <label className="input-label" style={{ color: "#ff6b3e" }}>Sua dica (1 palavra)</label>
                <div style={{ display: "flex", gap: 8 }}>
                  <input className="input-field" style={{ flex: 1, marginBottom: 0 }} placeholder="Ex: quente, doce, italiano..."
                    value={dica} onChange={e => { setDica(e.target.value); setErroDica(""); }}
                    onKeyDown={e => { if (e.key === "Enter") confirmarDica(); }} autoFocus />
                  <button className="btn btn-primary" onClick={confirmarDica} disabled={!dica.trim()}>→</button>
                </div>
              </div>
              {erroDica && <div style={{ color: "var(--accent)", fontSize: "0.8rem", marginTop: 6 }}>⚠️ {erroDica}</div>}
              {dicaEnviada && <div style={{ color: "var(--green)", fontSize: "0.8rem", marginTop: 6 }}>✅ Dica enviada!</div>}
            </>
          ) : (
            <div style={{ color: "var(--text-muted)", fontSize: "0.85rem" }}>⏳ Aguardando a dica de {dadorAtual.nome}...</div>
          )}
        </div>
      )}

      {/* Adivinhadores */}
      {(dados.pares || []).length > 0 && (
        <div style={{ background: "var(--bg-elevated)", borderRadius: 12, padding: "12px 16px" }}>
          <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", textTransform: "uppercase", marginBottom: 8 }}>🎯 Adivinhadores</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {dados.pares.map((p, i) => {
              const av = avStyle(p.adivinhador.idx);
              return (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, background: av.bg, border: `1px solid ${av.border}`, borderRadius: 20, padding: "5px 12px" }}>
                  <span>{p.adivinhador.avatar || av.emoji}</span>
                  <span style={{ fontSize: "0.82rem", fontWeight: 700, color: av.color }}>{p.adivinhador.nome}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Botão de acerto para adivinhadores */}
      {souAdivinhador && (
        <button style={{ background: "rgba(62,255,163,0.15)", border: "2px solid var(--green)", borderRadius: 12, padding: "16px", cursor: "pointer", color: "var(--green)", fontWeight: 700, fontSize: "1rem" }}
          onClick={onAcertou}>
          🎉 Eu sei a senha!
        </button>
      )}

      {/* Botão do host para encerrar sem acerto */}
      {isHost && (
        <button style={{ background: "var(--bg-elevated)", border: "1px solid var(--border)", borderRadius: 10, padding: "10px", cursor: "pointer", color: "var(--text-muted)", fontSize: "0.82rem" }}
          onClick={onNinguemAcertou}>
          ⏭️ Ninguém acertou — próxima palavra
        </button>
      )}
    </div>
  );
}

function MegaSenhaResultadoOnline({ dados, sala, isHost, onNovaRodada, onSair }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ textAlign: "center", padding: "8px 0" }}>
        <div style={{ fontSize: "3.5rem", marginBottom: 10 }}>{dados.acertouNome ? "🏆" : "⏰"}</div>
        <div style={{ fontFamily: "'Bebas Neue'", fontSize: "1.8rem", color: dados.acertouNome ? "#ff6b3e" : "var(--text-secondary)" }}>
          {dados.acertouNome ? `${dados.acertouNome} acertou!` : "Ninguém acertou!"}
        </div>
        <div style={{ background: "#ff6b3e15", border: "1px solid #ff6b3e40", borderRadius: 12, padding: "12px 18px", marginTop: 12 }}>
          <div style={{ fontSize: "0.72rem", color: "#ff6b3e", textTransform: "uppercase", marginBottom: 4 }}>🔐 A senha era</div>
          <div style={{ fontFamily: "'Bebas Neue'", fontSize: "2.4rem", color: "#ff6b3e" }}>{dados.senha}</div>
        </div>
      </div>

      {/* Dicas usadas */}
      {(dados.dicas || []).length > 0 && (
        <div style={{ background: "var(--bg-elevated)", borderRadius: 12, padding: "14px 16px" }}>
          <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", textTransform: "uppercase", marginBottom: 10 }}>💬 Dicas desta palavra</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {dados.dicas.map((d, i) => {
              const av = avStyle(d.dadorIdx);
              return (
                <span key={i} style={{ background: av.bg, border: `1px solid ${av.border}`, color: av.color, borderRadius: 20, padding: "4px 12px", fontSize: "0.82rem", fontWeight: 700 }}>
                  {d.dica}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Pontos acumulados */}
      {(dados.pares || []).length > 0 && (
        <div style={{ background: "var(--bg-elevated)", borderRadius: 12, padding: "14px 16px" }}>
          <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", textTransform: "uppercase", marginBottom: 10 }}>🏆 Pontos acumulados</div>
          {dados.pares.map((p, i) => {
            const av = avStyle(p.adivinhador.idx);
            const pts = dados.pontos?.[i] || 0;
            return (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 0", borderBottom: "1px solid var(--border)" }}>
                <div style={{ width: 36, height: 36, borderRadius: "50%", background: av.bg, border: `2px solid ${av.border}`, color: av.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.1rem" }}>
                  {p.adivinhador.avatar || av.emoji}
                </div>
                <span style={{ flex: 1, fontWeight: 700, color: av.color }}>{p.adivinhador.nome}</span>
                <span style={{ fontFamily: "'Bebas Neue'", fontSize: "1.3rem", color: pts > 0 ? "#ff6b3e" : "var(--text-muted)" }}>{pts} pt{pts !== 1 ? "s" : ""}</span>
              </div>
            );
          })}
        </div>
      )}

      <div style={{ display: "flex", gap: 10 }}>
        <button className="btn btn-secondary" style={{ flex: 1 }} onClick={onSair}>🏠 Sair</button>
        {isHost && <button className="btn btn-primary" style={{ flex: 1 }} onClick={onNovaRodada}>🔄 Nova palavra</button>}
        {!isHost && <div style={{ flex: 1, textAlign: "center", color: "var(--text-muted)", fontSize: "0.82rem", padding: "12px 0" }}>Aguardando host...</div>}
      </div>
    </div>
  );
}

// ── Modos originais ────────────────────────────────────────────────

function RespostaOnline({ onEnviar }) {
  const [resposta, setResposta] = useState("");
  const [enviado, setEnviado] = useState(false);
  if (enviado) return (
    <div style={{ textAlign: "center", padding: 24 }}>
      <div style={{ fontSize: "2.4rem", marginBottom: 12 }}>✅</div>
      <div style={{ fontFamily: "'Bebas Neue'", fontSize: "1.4rem", color: "var(--green)" }}>Resposta enviada!</div>
      <div style={{ color: "var(--text-secondary)", fontSize: "0.85rem", marginTop: 8 }}>Aguardando os outros jogadores...</div>
    </div>
  );
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <label className="input-label">✍️ Sua resposta</label>
      <textarea className="input-field" rows={3} placeholder="Digite sua resposta..." value={resposta}
        onChange={e => setResposta(e.target.value)} maxLength={280} />
      <button className="btn btn-primary btn-full" disabled={!resposta.trim()}
        onClick={() => { onEnviar(resposta.trim()); setEnviado(true); }}>
        ✅ Enviar resposta
      </button>
    </div>
  );
}

function ResultadosOnline({ dados, isHost, onIniciarVotacao }) {
  const CORES = ["#ff3e3e","#9b6dff","#3effa3","#f5c842","#ff6b3e","#3eaaff"];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ background: "var(--bg-elevated)", borderRadius: 12, padding: "14px 16px", borderLeft: "4px solid var(--accent)" }}>
        <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", textTransform: "uppercase", marginBottom: 6 }}>A pergunta era</div>
        <div style={{ fontWeight: 700, fontStyle: "italic" }}>"{dados.pergunta}"</div>
      </div>
      {dados.respostas.map((r, i) => {
        const cor = CORES[i % CORES.length];
        return (
          <div key={i} style={{ background: "var(--bg-elevated)", borderRadius: 12, padding: "14px 16px", borderLeft: `3px solid ${cor}` }}>
            <div style={{ fontWeight: 700, color: cor, marginBottom: 4, fontSize: "0.85rem" }}>{r.nome}</div>
            <div style={{ color: "var(--text-primary)" }}>{r.resposta}</div>
          </div>
        );
      })}
      {isHost && <button className="btn btn-primary btn-full" onClick={onIniciarVotacao}>🗳️ Iniciar votação →</button>}
      {!isHost && <div style={{ textAlign: "center", color: "var(--text-muted)", fontSize: "0.85rem" }}>⏳ Aguardando o host iniciar a votação...</div>}
    </div>
  );
}

function VotacaoOnline({ jogadores, jogadorId, onVotar, dadosVotos }) {
  const [votado, setVotado] = useState(false);
  const [selecionado, setSelecionado] = useState(null);
  if (votado) return (
    <div style={{ textAlign: "center", padding: 24 }}>
      <div style={{ fontSize: "2rem", marginBottom: 8 }}>🗳️</div>
      <div style={{ fontFamily: "'Bebas Neue'", fontSize: "1.4rem", color: "var(--purple)" }}>Voto registrado!</div>
      <div style={{ color: "var(--text-secondary)", fontSize: "0.85rem", marginTop: 6 }}>
        {dadosVotos?.votosRecebidos && `${dadosVotos.votosRecebidos}/${dadosVotos.esperando || jogadores.length} votos`}
      </div>
    </div>
  );
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ fontSize: "0.85rem", color: "var(--text-secondary)", marginBottom: 4 }}>🗳️ Vote em quem você acha que é o impostor:</div>
      {jogadores.filter(j => j.id !== jogadorId).map((j) => {
        const idx = jogadores.indexOf(j);
        const av = avStyle(idx);
        const sel = selecionado === j.id;
        return (
          <button key={j.id} onClick={() => setSelecionado(j.id)} style={{
            display: "flex", alignItems: "center", gap: 12,
            background: sel ? av.bg : "var(--bg-elevated)",
            border: `2px solid ${sel ? av.border : "var(--border)"}`,
            borderRadius: 12, padding: "12px 16px", cursor: "pointer",
          }}>
            <div style={{ width: 40, height: 40, borderRadius: "50%", background: av.bg, border: `2px solid ${av.border}`, color: av.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.3rem" }}>{j.avatar}</div>
            <span style={{ fontWeight: 700, flex: 1, textAlign: "left", color: sel ? av.color : "var(--text-primary)" }}>{j.nome}</span>
            {sel && <span style={{ color: av.color }}>✓</span>}
          </button>
        );
      })}
      <button className="btn btn-primary btn-full" disabled={!selecionado} onClick={() => {
        const idx = jogadores.findIndex(j => j.id === selecionado);
        onVotar(idx); setVotado(true);
      }}>🗳️ Confirmar voto</button>
    </div>
  );
}

function PlacarOnline({ dados, jogadores, isHost, onNovaRodada, onSair }) {
  const maisVotadoIdx = dados.contagem ? Object.entries(dados.contagem).sort((a,b) => b[1]-a[1])[0]?.[0] : null;
  const acertou = maisVotadoIdx !== null && dados.impostoresIdx?.includes(Number(maisVotadoIdx));
  const nomesImp = dados.impostoresIdx?.map(i => jogadores[i]?.nome || `Jogador ${i+1}`).join(" & ") || "?";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ textAlign: "center", padding: "16px 0" }}>
        <div style={{ fontSize: "3.5rem", marginBottom: 10 }}>{acertou ? "🎉" : "😈"}</div>
        <div style={{ fontFamily: "'Bebas Neue'", fontSize: "2rem", color: acertou ? "var(--green)" : "var(--accent)" }}>
          {acertou ? "Impostor desmascarado!" : "Impostor escapou!"}
        </div>
        <div style={{ color: "var(--text-secondary)", marginTop: 6 }}>
          {acertou ? `O grupo eliminou ${nomesImp}` : `O impostor era ${nomesImp}!`}
        </div>
      </div>
      <div style={{ background: "var(--bg-elevated)", borderRadius: 12, padding: "14px 16px" }}>
        <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", textTransform: "uppercase", marginBottom: 10 }}>Resultado da votação</div>
        {jogadores.map((j, idx) => {
          const votos = dados.contagem?.[idx] || 0;
          const ehImp = dados.impostoresIdx?.includes(idx);
          const av = avStyle(idx);
          return (
            <div key={idx} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0", borderBottom: "1px solid var(--border)" }}>
              <div style={{ width: 36, height: 36, borderRadius: "50%", background: av.bg, border: `2px solid ${av.border}`, color: av.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.1rem" }}>{j.avatar}</div>
              <span style={{ flex: 1, color: ehImp ? "var(--accent)" : "var(--text-primary)", fontWeight: ehImp ? 700 : 400 }}>
                {j.nome} {ehImp && "🕵️"}
              </span>
              <span style={{ fontFamily: "'Space Mono'", color: "var(--text-muted)", fontSize: "0.85rem" }}>{votos} voto{votos !== 1 ? "s" : ""}</span>
            </div>
          );
        })}
      </div>
      <div style={{ display: "flex", gap: 10 }}>
        <button className="btn btn-secondary" style={{ flex: 1 }} onClick={onSair}>🏠 Sair</button>
        {isHost && <button className="btn btn-primary" style={{ flex: 1 }} onClick={onNovaRodada}>🔄 Nova rodada</button>}
        {!isHost && <div style={{ flex: 1, textAlign: "center", color: "var(--text-muted)", fontSize: "0.82rem", padding: "12px 0" }}>Aguardando host...</div>}
      </div>
    </div>
  );
}
